import { inject, injectable } from 'tsyringe';
import { FormatService } from '../FormatService';
import { cleanQueryDates, exprBuilder, groupExprs, IFluentOperators, queryBuilder } from '../QueryExpr';
import { QueryOperation, QueryResult } from '@apis/Resources';
import { InvoiceApiService } from './InvoiceApiService';
import { eachMonthOfInterval, lastDayOfMonth } from 'date-fns';
import { MdsHistoricalRecord, Query } from '@apis/Invoices/model';

type MdsRecord = Omit<Defined<MdsHistoricalRecord>, 'Date'> & { Date: Date };

@injectable()
export class DiscountQueryService {
    public get invoiceApi() {
        return this._invoiceApi;
    }

    public constructor(
        @inject(FormatService) private readonly fmtSvc: FormatService,
        @inject(InvoiceApiService) private readonly _invoiceApi: InvoiceApiService
    ) {}

    public async getConnectedPlatforms() {
        try {
            const results = await queryBuilder<MdsRecord>()
                .select((b) => ({ platform: b.model.Platform!, ct: b.count() }))
                .execute((q) => this.invoiceApi.queryDiscountsData(q));
            return results?.Results?.map((r) => r.platform) ?? [];
        } catch (error) {
            console.error('Error fetching connected platforms:', error);
            return [];
        }
    }

    public queryBuilder() {
        return queryBuilder<MdsRecord>();
    }

    public async query<T>(platform: string, query: Query, fromMonth: Date, toMonth: Date): Promise<QueryResult<T>>;
    public async query<T>(platform: string, query: Query): Promise<QueryResult<T>>;
    public async query<T>(platform: string, query: Query, fromMonth?: Date, toMonth?: Date) {
        const queryApi = this.scopeApi<T>(platform, fromMonth, toMonth);
        return queryApi(query) as Promise<QueryResult<T>>;
    }

    public scopeApi<T>(platform: string, fromMonth?: Date, toMonth?: Date) {
        const { builder: xb } = exprBuilder<MdsRecord>();
        const endOfMonth = toMonth ? lastDayOfMonth(toMonth) : null;
        const startOfMonth = fromMonth ? new Date(fromMonth.getFullYear(), fromMonth.getMonth(), 1) : null;
        return (query: Query) => {
            const criteria: IFluentOperators<boolean>[] = [];
            if (startOfMonth) {
                criteria.push(xb.model.Date!.onOrAfter(startOfMonth));
            }
            if (endOfMonth) {
                criteria.push(xb.model.Date!.onOrBefore(endOfMonth));
            }
            if (platform !== 'All') {
                criteria.push(xb.model.Platform!.eq(platform));
            }
            query = cleanQueryDates({ ...query, Where: groupExprs('and', [query.Where, ...criteria.map((v) => xb.resolve(v))]) });
            return this.invoiceApi.queryDiscountsData(query) as Promise<QueryResult<T>>;
        };
    }

    /** Get months for which discount data is available */
    public async getDiscountsRange(platform: string) {
        const qb = queryBuilder<MdsRecord>();
        if (platform !== 'All') {
            const platformCriteria: QueryOperation = {
                Operation: 'eq',
                Operands: [{ Field: 'Platform' }, { Value: platform }],
            };
            qb.where((b) => b.fromExpr(platformCriteria));
        }

        const results = await qb
            .select((b) => ({
                minDate: b.min(b.model.Date),
                maxDate: b.max(b.model.Date),
            }))
            .execute((q) => this.invoiceApi.queryDiscountsData(q));

        const { minDate, maxDate } = results?.Results?.[0] ?? {};
        const months: Date[] = [];
        if (minDate && maxDate && minDate !== maxDate) {
            months.push(
                ...eachMonthOfInterval({
                    start: this.fmtSvc.parseDateNoTime(minDate),
                    end: this.fmtSvc.parseDateNoTime(maxDate),
                })
            );
        }

        return { months };
    }
}
